/**
 * The ambience driver (R35/D115) — the session view's ear, as a hook: keep
 * the shared player aimed at the room's sound.
 *
 * @remarks
 * The sound is the VIEW's (D156): `resolveRoomAmbience` ran wherever the
 * turn ran and its target arrived resolved, so the driver only aims the
 * player, and the player's own key check makes repeats free. `'silence'`
 * and no-layer both aim the player at quiet — authored silence
 * and an absent layer sound identical; the difference is editorial, not
 * audible — but only after something has played (the title theme, an earlier
 * bed) is there anything to fade.
 *
 * @packageDocumentation
 */

import type { AmbienceTarget } from '@orpheus/api-contracts';

import { useEffect } from 'react';

import { type AmbiencePlayer, ambientGainFromSetting } from './ambience-player.js';

/**
 * Drive the ambience player from the view's target.
 *
 * @param ambiencePlayer - The shell's player, when it has one
 * @param ambience - The view's bed by URL, deliberate quiet, or nothing when the pack gives the room no sound at all
 * @param isAmbientAudioEnabled - The player's on/off preference
 * @param ambientAudioVolume - The player's loudness setting (0..1)
 */
export const useAmbienceDriver = (
	ambiencePlayer: AmbiencePlayer | undefined,
	ambience: AmbienceTarget | undefined,
	isAmbientAudioEnabled: boolean,
	ambientAudioVolume: number,
): void => {
	useEffect(() => {
		ambiencePlayer?.setEnabled(isAmbientAudioEnabled);
	}, [ambiencePlayer, isAmbientAudioEnabled]);

	// D116/D117: the stored preference is the control's SETTING; the shared
	// taper (`ambientGainFromSetting`) turns it into gain on the way in.
	useEffect(() => {
		ambiencePlayer?.setVolume(ambientGainFromSetting(ambientAudioVolume));
	}, [ambiencePlayer, ambientAudioVolume]);

	// The target is the view's, already resolved (D156): a repeated identical
	// target no-ops inside the player, so this runs on every render safely.
	const targetKey = ambience === undefined || ambience === 'silence' ? 'silence' : ambience.key;
	const targetUrl = ambience === undefined || ambience === 'silence' ? undefined : ambience.url;

	useEffect(() => {
		if (ambiencePlayer === undefined) {
			return;
		}

		ambiencePlayer.setTarget(targetUrl === undefined ? 'silence' : { key: targetKey, url: targetUrl });
	}, [ambiencePlayer, targetKey, targetUrl]);
};
